import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';

/**
 * PageSkeleton — the app's loading placeholder, shown while a page's code chunk
 * or first data load is in flight. A soft shimmer of the shape a page usually
 * has (title + action, a row of stat tiles, a table) reads as "content is
 * coming" far better than a bare spinner, and it matches on every route.
 *
 * `variant` lets a caller lean the shimmer toward what it is loading:
 *   'page'  — title + tiles + table (the default, good for list/detail pages)
 *   'table' — just the table block
 *   'form'  — a two-column field grid
 */
export function PageSkeleton({ variant = 'page' }: { variant?: 'page' | 'table' | 'form' }) {
  const bar = (w: number | string, h = 14, r = 1) => (
    <Skeleton variant="rounded" animation="wave" width={w} height={h} sx={{ borderRadius: r }} />
  );

  const table = (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', gap: 2, px: 2, py: 1.25, bgcolor: 'action.hover' }}>
        {[180, 120, 120, 100, 90].map((w, i) => <Box key={i} sx={{ flex: w / 100 }}>{bar('70%', 12)}</Box>)}
      </Box>
      {Array.from({ length: 7 }).map((_, r) => (
        <Box key={r} sx={{ display: 'flex', gap: 2, px: 2, py: 1.5,
          borderTop: '1px solid', borderColor: 'divider' }}>
          {[180, 120, 120, 100, 90].map((w, i) => (
            <Box key={i} sx={{ flex: w / 100 }}>{bar(`${55 + ((r + i) % 4) * 10}%`, 12)}</Box>
          ))}
        </Box>
      ))}
    </Box>
  );

  if (variant === 'table') {
    return <Box sx={{ animation: 'uknFadeIn 200ms ease' }}>{table}</Box>;
  }

  if (variant === 'form') {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5, maxWidth: 760 }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {bar('40%', 10)}
            {bar('100%', 38, 1.5)}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3,
      '@keyframes uknFadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
      animation: 'uknFadeIn 220ms ease' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {bar(220, 22)}
          {bar(320, 12)}
        </Box>
        {bar(130, 36, 2)}
      </Box>
      <Box sx={{ display: 'grid', gap: 2,
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' } }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" animation="wave" height={84} sx={{ borderRadius: 2 }} />
        ))}
      </Box>
      {table}
    </Box>
  );
}
